import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Cloud, 
  CloudRain, 
  Sun, 
  Wind,
  AlertTriangle,
  TrendingUp,
  Clock,
  Zap,
  Thermometer,
  Droplets,
  Eye,
  Calendar,
  CheckCircle,
  Sparkles,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Dna,
  Leaf,
  Target,
  BarChart3,
  FlaskConical,
  Star,
  Gem
} from 'lucide-react'
import { useApi } from '../contexts/ApiContext'
import { formatDate, formatTimeLeft, getRarityColor, getRarityBadgeColor, formatRarity, calculateMutationWeatherSchedule, getMutationWeatherStatusText, getMutationWeatherStatusColor, getMutationWeatherIcon } from '../lib/utils'
import { cn } from '../lib/utils'
import { weatherEffects } from '../lib/gameGuide'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const statVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 }
}

// Mutation types and their effects
const mutationTypes = {
  golden: { 
    name: "Golden Mutation", 
    rarity: "legendary", 
    valueMultiplier: 5.0, 
    description: "Rare golden variant with exceptional value",
    icon: Star,
    color: "from-yellow-400 to-yellow-600"
  },
  rainbow: { 
    name: "Rainbow Mutation", 
    rarity: "mythic", 
    valueMultiplier: 10.0, 
    description: "Mythical rainbow variant with incredible value",
    icon: Sparkles,
    color: "from-pink-400 to-purple-600"
  },
  crystal: { 
    name: "Crystal Mutation", 
    rarity: "divine", 
    valueMultiplier: 25.0, 
    description: "Divine crystal variant with extraordinary value",
    icon: Gem,
    color: "from-blue-400 to-cyan-600"
  },
  void: { 
    name: "Void Mutation", 
    rarity: "mythical", 
    valueMultiplier: 50.0, 
    description: "Mythical void variant with incomprehensible value",
    icon: Dna,
    color: "from-purple-600 to-black"
  },
  prismatic: { 
    name: "Prismatic Mutation", 
    rarity: "prismatic", 
    valueMultiplier: 100.0, 
    description: "Prismatic variant beyond reality",
    icon: Sparkles,
    color: "from-rainbow to-rainbow"
  }
}

// Weather conditions that boost mutation chances
const mutationWeatherBoosts = {
  stormy: { chance: 0.15, description: "Stormy weather increases mutation chances" },
  rainy: { chance: 0.10, description: "Rainy weather slightly boosts mutations" },
  sunny: { chance: 0.05, description: "Sunny weather provides minor mutation boost" },
  windy: { chance: 0.08, description: "Windy conditions can trigger mutations" },
  snowy: { chance: 0.12, description: "Snowy weather has good mutation potential" },
  cloudy: { chance: 0.02, description: "Cloudy weather has minimal mutation effect" }
}

export default function MutationTracker() {
  const { isAuthenticated, fetchWeather, calculateFruit } = useApi()
  const [selectedFruit, setSelectedFruit] = useState('Apple')
  const [fruitWeight, setFruitWeight] = useState('1.0')
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000))

  // Real-time countdown timer for mutation weather
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000))
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Calculate mutation weather schedule
  const mutationWeatherSchedule = calculateMutationWeatherSchedule(currentTime)

  const { data: weatherData, isLoading, refetch } = useQuery({
    queryKey: ['weather'],
    queryFn: fetchWeather,
    enabled: isAuthenticated,
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // Mutation weather notifications
  useEffect(() => {
    if (!isAuthenticated) return

    // Check if mutation weather just started (within last 30 seconds)
    if (mutationWeatherSchedule.isActive && mutationWeatherSchedule.currentPeak) {
      const timeSinceStart = currentTime - mutationWeatherSchedule.currentPeak.time
      if (timeSinceStart <= 30) {
        // This would trigger a notification - you can add notification context here
        console.log(`🎯 ${mutationWeatherSchedule.currentPeak.weatherType} mutation peak started!`)
      }
    }

    // Check if mutation weather is about to end (within 5 minutes)
    if (mutationWeatherSchedule.isActive && mutationWeatherSchedule.currentPeak?.endTime) {
      const timeUntilEnd = mutationWeatherSchedule.currentPeak.endTime - currentTime
      
      if (timeUntilEnd <= 300 && timeUntilEnd > 240) { // Between 5 and 4 minutes
        console.log(`⚠️ ${mutationWeatherSchedule.currentPeak.weatherType} mutation peak ending in 5 minutes!`)
      }
    }

    // Check if mutation weather is starting soon (within 10 minutes)
    if (!mutationWeatherSchedule.isActive && mutationWeatherSchedule.timeUntilNext <= 600 && mutationWeatherSchedule.timeUntilNext > 540) {
      console.log(`ℹ️ ${mutationWeatherSchedule.nextPeak?.weatherType} mutation peak starting in ${Math.floor(mutationWeatherSchedule.timeUntilNext / 60)} minutes!`)
    }
  }, [currentTime, mutationWeatherSchedule, isAuthenticated])

  // Calculate mutation chances based on current weather
  const mutationAnalysis = useMemo(() => {
    if (!weatherData?.weather) return null

    const activeWeather = weatherData.weather.filter(w => w.active)
    let totalMutationChance = 0.02 // Base mutation chance
    let weatherEffects: Array<{
      weather: string
      boost: number
      description: string
    }> = []

    activeWeather.forEach(weather => {
      const weatherType = weather.weather_name.toLowerCase()
      const boost = mutationWeatherBoosts[weatherType as keyof typeof mutationWeatherBoosts]
      
      if (boost) {
        totalMutationChance += boost.chance
        weatherEffects.push({
          weather: weather.weather_name,
          boost: boost.chance,
          description: boost.description
        })
      }
    })

    return {
      totalChance: Math.min(totalMutationChance, 0.5), // Cap at 50%
      weatherEffects,
      activeWeather: activeWeather.length,
      recommendations: generateMutationRecommendations(activeWeather)
    }
  }, [weatherData])

  function generateMutationRecommendations(activeWeather: any[]) {
    const recommendations = []
    
    if (activeWeather.length === 0) {
      recommendations.push({
        type: 'info',
        message: 'No special weather conditions. Base mutation chance is 2%.',
        icon: Leaf,
        color: 'text-info'
      })
    }

    if (activeWeather.some(w => w.weather_name.toLowerCase().includes('storm'))) {
      recommendations.push({
        type: 'success',
        message: 'Stormy weather detected! High mutation chance - plant rare seeds now!',
        icon: Zap,
        color: 'text-success'
      })
    }

    if (activeWeather.some(w => w.weather_name.toLowerCase().includes('rain'))) {
      recommendations.push({
        type: 'success',
        message: 'Rainy weather active! Good mutation conditions for water-loving plants.',
        icon: CloudRain,
        color: 'text-success'
      })
    }

    if (activeWeather.some(w => w.weather_name.toLowerCase().includes('snow'))) {
      recommendations.push({
        type: 'warning',
        message: 'Snowy weather detected! Good for mutations but slower growth.',
        icon: Thermometer,
        color: 'text-warning'
      })
    }

    return recommendations
  }

  if (!isAuthenticated) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <div className="text-center max-w-md space-modern">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-r from-purple to-purple/80 flex items-center justify-center mb-4">
              <Dna className="h-8 w-8 text-primary-foreground" />
            </div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Mutation Tracker</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Please configure your API key using environment variables to view mutation and weather data.
          </p>
          <div className="glass-card p-6 text-left mt-6">
            <p className="text-sm font-medium mb-3">Setup Instructions:</p>
            <ol className="text-xs text-muted-foreground space-y-2">
              <li>1. Create a <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">.env</code> file in the project root</li>
              <li>2. Add <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">VITE_API_KEY=your_api_key</code></li>
              <li>3. Restart the development server</li>
            </ol>
          </div>
        </div>
      </motion.div>
    )
  }

  const activeWeather = weatherData?.weather?.filter(w => w.active) || []
  const recentWeather = weatherData?.weather?.filter(w => !w.active).slice(0, 5) || []

  // Calculate weather statistics
  const totalWeatherEvents = weatherData?.weather?.length || 0
  const activeWeatherCount = activeWeather.length
  const averageDuration = weatherData?.weather 
    ? Math.round(weatherData.weather.reduce((sum, w) => sum + (w.end_duration_unix - w.start_duration_unix), 0) / weatherData.weather.length / 3600)
    : 0

  const mutationStats = [
    {
      name: 'Mutation Chance',
      value: mutationAnalysis ? `${Math.round(mutationAnalysis.totalChance * 100)}%` : '2%',
      icon: Dna,
      color: 'from-purple to-purple/80',
      bgColor: 'bg-purple/10',
      borderColor: 'border-purple/30',
      description: 'Current mutation probability',
      trend: mutationAnalysis && mutationAnalysis.totalChance > 0.02 ? '+15%' : '0%',
      trendUp: mutationAnalysis && mutationAnalysis.totalChance > 0.02
    },
    {
      name: 'Active Weather',
      value: activeWeatherCount,
      icon: CloudRain,
      color: 'from-success to-success/80',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/30',
      description: 'Weather conditions affecting mutations',
      trend: '+2',
      trendUp: true
    },
    {
      name: 'Weather Events',
      value: totalWeatherEvents,
      icon: BarChart3,
      color: 'from-info to-info/80',
      bgColor: 'bg-info/10',
      borderColor: 'border-info/30',
      description: 'Total weather events tracked',
      trend: '+5',
      trendUp: true
    },
  ]

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-modern"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-modern-sm">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-purple to-purple/80 shadow-lg">
              <Dna className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                Mutation Tracker
              </h1>
              <p className="text-muted-foreground leading-relaxed">
                Monitor weather conditions and their effects on mutation chances
              </p>
            </div>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => refetch()}
          disabled={isLoading}
          className="btn btn-outline btn-md"
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
          Refresh
        </motion.button>
      </div>

      {/* Mutation Weather Status */}
      <motion.div variants={itemVariants} className="gaming-card accent-purple">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple/20 to-purple/10 border border-purple/30">
                <div className="w-6 h-6 text-purple">{getMutationWeatherIcon(mutationWeatherSchedule.bestWeatherType || 'default')}</div>
              </div>
              <h3 className="card-title text-purple">Mutation Weather Sync</h3>
            </div>
            <div className="text-right">
              <p className={cn("text-sm font-semibold", getMutationWeatherStatusColor(mutationWeatherSchedule))}>
                {getMutationWeatherStatusText(mutationWeatherSchedule)}
              </p>
              <p className="text-xs text-muted-foreground">
                Max Chance: {Math.round(mutationWeatherSchedule.maxChance * 100)}%
              </p>
            </div>
          </div>
          <div className="mt-2">
            {mutationWeatherSchedule.isActive ? (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  {mutationWeatherSchedule.currentPeak?.weatherType} mutation peak active
                </p>
                <p className="text-xs text-muted-foreground">
                  Peak Schedule: Every 4 hours • Duration: 2-4 hours
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  Next peak: {mutationWeatherSchedule.nextPeak?.weatherType} at {formatDate(mutationWeatherSchedule.nextPeak?.time || 0)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Schedule: Every 4 hours • Duration: 2-4 hours
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Mutation Stats */}
      <div className="grid-modern md:grid-cols-3">
        {mutationStats.map((stat, index) => (
          <motion.div
            key={stat.name}
            variants={statVariants}
            className="gaming-card hover-lift group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={cn("p-3 rounded-xl", stat.bgColor, stat.borderColor, "border")}>
                <stat.icon className={cn("h-6 w-6", stat.color.replace('from-', 'text-').replace(' to-', ''))} />
              </div>
              <div className="flex items-center space-x-1">
                {stat.trendUp ? (
                  <ArrowUpRight className="h-4 w-4 text-success" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-destructive" />
                )}
                <span className={cn("text-xs font-medium", stat.trendUp ? "text-success" : "text-destructive")}>
                  {stat.trend}
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                {stat.name}
              </p>
              <p className="text-2xl font-bold tracking-tight">
                {isLoading ? (
                  <div className="h-8 w-20 bg-muted/50 rounded-lg shimmer"></div>
                ) : (
                  stat.value
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {stat.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Mutation Recommendations */}
      {mutationAnalysis?.recommendations && mutationAnalysis.recommendations.length > 0 && (
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-warning/20 to-warning/10 border border-warning/30">
                <Target className="h-6 w-6 text-warning" />
              </div>
              <h3 className="card-title">Mutation Recommendations</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="space-y-3">
              {mutationAnalysis.recommendations.map((rec, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={cn(
                    "flex items-center space-x-3 p-4 rounded-xl border transition-modern",
                    rec.type === 'warning' && "bg-warning/10 border-warning/30",
                    rec.type === 'success' && "bg-success/10 border-success/30",
                    rec.type === 'info' && "bg-info/10 border-info/30"
                  )}
                >
                  <rec.icon className={cn("h-5 w-5 flex-shrink-0", rec.color)} />
                  <p className="text-sm font-medium">{rec.message}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid-modern lg:grid-cols-2">
        {/* Current Weather & Mutation Effects */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-success/20 to-success/10 border border-success/30">
                <Sun className="h-6 w-6 text-success" />
              </div>
              <h3 className="card-title">Current Weather & Mutations</h3>
            </div>
          </div>
          <div className="card-content">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="loading-spinner h-8 w-8"></div>
              </div>
            ) : activeWeather.length > 0 ? (
              <div className="space-modern-sm">
                {activeWeather.map((weather, index) => {
                  const weatherType = weather.weather_name.toLowerCase()
                  const effects = weatherEffects[weatherType as keyof typeof weatherEffects]
                  const mutationBoost = mutationWeatherBoosts[weatherType as keyof typeof mutationWeatherBoosts]
                  
                  return (
                    <motion.div
                      key={weather.weather_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 hover:scale-[1.02]"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          {weather.icon && (
                            <div className="p-2 rounded-xl bg-muted/50 border border-border/30">
                              <img 
                                src={weather.icon} 
                                alt={weather.weather_name} 
                                className="w-8 h-8"
                              />
                            </div>
                          )}
                          <div>
                            <p className="font-semibold">{weather.weather_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {effects?.description || 'Weather condition active'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">
                            {formatTimeLeft(weather.end_duration_unix)}
                          </p>
                          <p className="text-xs text-muted-foreground">remaining</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {effects && (
                          <div className="p-3 rounded-lg bg-success/10 border border-success/30">
                            <div className="flex items-center space-x-2 mb-1">
                              <TrendingUp className="h-4 w-4 text-success" />
                              <span className="text-xs font-medium text-success">Growth Boost</span>
                            </div>
                            <p className="text-lg font-bold text-success">
                              +{Math.round((effects.growth - 1) * 100)}%
                            </p>
                          </div>
                        )}
                        {mutationBoost && (
                          <div className="p-3 rounded-lg bg-purple/10 border border-purple/30">
                            <div className="flex items-center space-x-2 mb-1">
                              <Dna className="h-4 w-4 text-purple" />
                              <span className="text-xs font-medium text-purple">Mutation Boost</span>
                            </div>
                            <p className="text-lg font-bold text-purple">
                              +{Math.round(mutationBoost.chance * 100)}%
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <div className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center">
                    <Sun className="h-6 w-6 opacity-50" />
                  </div>
                  <p>Clear skies - normal growing conditions</p>
                  <p className="text-xs">Base mutation chance: 2%</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Mutation Types Guide */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-pink/20 to-pink/10 border border-pink/30">
                <Sparkles className="h-6 w-6 text-pink" />
              </div>
              <h3 className="card-title">Mutation Types</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="space-modern-sm">
              {Object.entries(mutationTypes).slice(0, 4).map(([key, mutation], index) => (
                <motion.div
                  key={key}
                  whileHover={{ scale: 1.02 }}
                  className="p-3 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={cn("p-1 rounded-lg bg-gradient-to-r", mutation.color)}>
                        <mutation.icon className="h-4 w-4 text-white" />
                      </div>
                      <h4 className="font-semibold text-sm">{mutation.name}</h4>
                    </div>
                    <span className={cn("text-xs px-2 py-1 rounded-lg", getRarityBadgeColor(mutation.rarity))}>
                      {mutation.rarity}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{mutation.description}</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs bg-purple/10 text-purple px-2 py-1 rounded-lg">
                      {mutation.valueMultiplier}x Value
                    </span>
                  </div>
                </motion.div>
              ))}
              <div className="text-center pt-2">
                <button className="text-sm text-muted-foreground hover:text-foreground transition-modern font-medium">
                  View all mutation types →
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Weather Forecast */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-info/20 to-info/10 border border-info/30">
                <Calendar className="h-6 w-6 text-info" />
              </div>
              <h3 className="card-title">Weather Forecast</h3>
            </div>
          </div>
          <div className="card-content">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="loading-spinner h-8 w-8"></div>
              </div>
            ) : weatherData?.forecast && weatherData.forecast.length > 0 ? (
              <div className="space-modern-sm">
                {weatherData.forecast.slice(0, 5).map((forecast, index) => {
                  const weatherType = forecast.weather_name.toLowerCase()
                  const mutationBoost = mutationWeatherBoosts[weatherType as keyof typeof mutationWeatherBoosts]
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 hover:scale-[1.02]"
                    >
                      <div className="flex items-center space-x-3">
                        {forecast.icon && (
                          <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                            <img src={forecast.icon} alt={forecast.weather_name} className="w-6 h-6" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm">{forecast.weather_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(forecast.start_duration_unix * 1000)}
                          </p>
                          {mutationBoost && (
                            <p className="text-xs text-purple">
                              +{Math.round(mutationBoost.chance * 100)}% mutation chance
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">
                          {formatTimeLeft(forecast.end_duration_unix)}
                        </p>
                        <p className="text-xs text-muted-foreground">duration</p>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <div className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center">
                    <Calendar className="h-6 w-6 opacity-50" />
                  </div>
                  <p>No forecast available</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Mutation Calculator */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-orange/20 to-orange/10 border border-orange/30">
                <FlaskConical className="h-6 w-6 text-orange" />
              </div>
              <h3 className="card-title">Mutation Calculator</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Fruit Type
                </label>
                <select 
                  value={selectedFruit}
                  onChange={(e) => setSelectedFruit(e.target.value)}
                  className="w-full p-3 rounded-xl bg-muted/20 border border-border/30 focus:border-primary transition-modern"
                >
                  <option value="Apple">Apple</option>
                  <option value="Orange">Orange</option>
                  <option value="Banana">Banana</option>
                  <option value="Strawberry">Strawberry</option>
                  <option value="Grape">Grape</option>
                  <option value="Watermelon">Watermelon</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Weight (kg)
                </label>
                <input 
                  type="number"
                  step="0.1"
                  value={fruitWeight}
                  onChange={(e) => setFruitWeight(e.target.value)}
                  className="w-full p-3 rounded-xl bg-muted/20 border border-border/30 focus:border-primary transition-modern"
                  placeholder="1.0"
                />
              </div>

              <div className="p-4 rounded-xl bg-purple/10 border border-purple/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Dna className="h-4 w-4 text-purple" />
                  <span className="text-sm font-medium text-purple">Current Mutation Chance</span>
                </div>
                <p className="text-2xl font-bold text-purple">
                  {mutationAnalysis ? `${Math.round(mutationAnalysis.totalChance * 100)}%` : '2%'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Based on current weather conditions
                </p>
              </div>

              <button className="w-full btn btn-primary btn-md">
                Calculate Value
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
} 